import type {
  CreateInviteRequest,
  CreateInviteResponse,
  CreateReviewRequest,
  CreateReviewResponse,
  InviteListResponse,
  LogAttemptRequest,
  Me,
  MyReviewsResponse,
  RedeemRequest,
  RedeemResponse,
  Stats,
  UploadUrlResponse,
  AdminReviewsResponse,
} from "@coterie/types";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

const req = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  const res = await fetch(path, { credentials: "include", ...init, headers });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (body as { error?: string }).error ?? `http ${res.status}`);
  return body as T;
};

export const api = {
  me: () => req<Me>("/api/me"),
  myReviews: (q: { limit?: number; cursor?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.limit) p.set("limit", String(q.limit));
    if (q.cursor) p.set("cursor", q.cursor);
    const qs = p.toString();
    return req<MyReviewsResponse>(`/api/me/reviews${qs ? `?${qs}` : ""}`);
  },
  stats: () => req<Stats>("/api/stats"),
  redeem: (body: RedeemRequest) =>
    req<RedeemResponse>("/api/auth/redeem", { method: "POST", body: JSON.stringify(body) }),
  uploadUrl: () =>
    req<UploadUrlResponse>("/api/reviews/upload-url", {
      method: "POST",
      body: JSON.stringify({ contentType: "image/jpeg" }),
    }),
  review: (body: CreateReviewRequest) =>
    req<CreateReviewResponse>("/api/reviews", { method: "POST", body: JSON.stringify(body) }),
  attempt: (body: LogAttemptRequest, keepalive = false) =>
    req<void>("/api/attempts", { method: "POST", body: JSON.stringify(body), keepalive }),
};

const adminReq = async <T>(token: string, path: string, init: RequestInit = {}) =>
  req<T>(path, { ...init, headers: { authorization: `Bearer ${token}`, ...init.headers } });

export const adminApi = {
  list: (token: string) => adminReq<InviteListResponse>(token, "/api/admin/invites"),
  reviews: (token: string, q: { limit?: number; cursor?: string } = {}) => {
    const p = new URLSearchParams();
    if (q.limit) p.set("limit", String(q.limit));
    if (q.cursor) p.set("cursor", q.cursor);
    const qs = p.toString();
    return adminReq<AdminReviewsResponse>(token, `/api/admin/reviews${qs ? `?${qs}` : ""}`);
  },
  mint: (token: string, body: CreateInviteRequest) =>
    adminReq<CreateInviteResponse>(token, "/api/admin/invites", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

export const putPhoto = async (url: string, blob: Blob) => {
  const res = await fetch(url, { method: "PUT", headers: { "content-type": "image/jpeg" }, body: blob });
  if (!res.ok) throw new Error(`upload ${res.status}`);
};
